import { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import { Alert, Button, Dialog, DialogTitle, DialogContent, IconButton, Tooltip } from '@mui/material';
import CustomDataGrid from '../../../../../reusable-components/datagrid/CustomDataGrid';
import Iconify from '../../../../../reusable-components/iconify';
import { COPY_ORDER } from '../../../../../reusable-components/datagrid/orderColumns';
import { getOrdersForCopy, copyOrderItems } from '../../../../../api/order-sheet';
import PreviousOrderInfo from './PreviousOrderInfo';
import CloseIconButton from '../../../../../reusable-components/basic-buttons/CloseIconButton';
import { ShopCartContext, ShopDataContext } from '../_ShopViewContext';

export default function CopyOrder() {
  const { facilityId = '' } = useParams();
  const urlParams = new URLSearchParams(window.location.search);
  const categoryId = urlParams.get('categoryId');
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [isPunchoutItemsDialogOpen, setIsPunchoutItemsDialogOpen] = useState(false);
  const [punchoutItemsInCopiedOrder, setPunchoutItemsInCopiedOrder] = useState([]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const { cart, setCart } = useContext(ShopCartContext);
  const { data: items, setData: setItems } = useContext(ShopDataContext);

  const handleCopy = async (orderId) => {
    try {
      const response = await copyOrderItems(orderId);
      const punchoutItems = response.data.filter(item => item.copyProductId === -1);
      const nonPunchoutItems = response.data.filter(item => item.copyProductId !== -1);
      setPunchoutItemsInCopiedOrder(punchoutItems);
      const tempCart = {};
      const invalidItems = [];
      const validItems = [];
      nonPunchoutItems.forEach(itemFromOrder => {
        const dataItem = items.find(i => i.id === itemFromOrder.copyProductId);
        if(dataItem) {
          tempCart[dataItem.id] = itemFromOrder.quantity;
          validItems.push({...dataItem, quantity: itemFromOrder.quantity});
        }
        else invalidItems.push(itemFromOrder);
      })
      const validItemIds = validItems.map(i => i.id);
      const existingNonInvalidItems = items.filter(item => !item.invalidItem);
      const updatedData = [...invalidItems.map((item, index) => ({
        ...item,
        quantity: 0,
        invalidItem: true,
        id: -(index + 1),
        invalidItemQuantity: item.quantity,
        displayCategory: {},
        displaySubcategory: {},
        productDetails: [{
          pictures: [],
          uomType: item.uomType,
          uomAmount: item.uomAmount
        }]
      })),
      ...validItems,
      ...existingNonInvalidItems.filter(item => !validItemIds.includes(item.id))
    ];

      setItems(updatedData);
      setCart({...cart, ...tempCart});
    } catch (error) {
      enqueueSnackbar("Failed to copy order items", { variant: "error" });
    }
    handleClose();
  };


  useEffect(() => {
    if(punchoutItemsInCopiedOrder?.length) {
      setIsPunchoutItemsDialogOpen(true);
    }
  }, [punchoutItemsInCopiedOrder]);

  useEffect(() => {
    if (open) {
      getOrdersForCopy(categoryId, facilityId)
        .then(res => setData(res.data ?? []))
        .catch(() => enqueueSnackbar("Failed to load orders for copy", { varient: "error" }))
    }
  }, [open]);

  const actions = {
    field: 'actions',
    headerName: 'Actions',
    disableReorder: true,
    width: 100,
    renderCell: (params) => <>
      <PreviousOrderInfo row={params.row} />
      <Tooltip title="Copy Order" arrow>
        <IconButton onClick={() => handleCopy(params.row.orderId)} color="secondary">
          <Iconify icon="mdi:content-copy" />
        </IconButton>
      </Tooltip>
    </>,
    id: 'actions',
    className: 'actions',
  };

  return (
    <div>
      <Button
        variant="text"
        disableElevation
        sx={{ textDecoration: 'underline' }}
        onClick={handleOpen}
        color="secondary"
        endIcon={<Iconify icon="ic:round-file-copy" width={13} height={13} />}
      >
        Copy Previous Order
      </Button>

      <Dialog open={open} onClose={(event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        handleClose();
      }} maxWidth="md" fullWidth>
        <DialogTitle sx={{ textAlign: 'center' }}>
          Select Order to Copy
          <CloseIconButton onClick={handleClose} />
        </DialogTitle>
        <DialogContent>
          <CustomDataGrid
            gridId="orders-new-order-copy"
            boxSX={{ height: 'calc(100vh - 170px)' }}
            data={data ?? []}
            gridColumns={[...COPY_ORDER, actions]}
            getRowId={(row) => row.orderId}
            isLoading={data === null}
            sort={[{ field: 'orderedOn', sort: 'desc' }]}
            isModal
          />
        </DialogContent>
      </Dialog>

      <Dialog open={isPunchoutItemsDialogOpen} onClose={(event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        setIsPunchoutItemsDialogOpen(false);
      }} maxWidth="md" fullWidth>
        <DialogTitle sx={{ textAlign: 'center' }} color="info.main">
          Cannot Copy Punchout Items
          <CloseIconButton onClick={() => setIsPunchoutItemsDialogOpen(false)} />
          <Alert severity="warning" sx={{ justifyContent: 'center' }}>
            The following punchout items were not copied to your cart
          </Alert>
        </DialogTitle>
        <DialogContent>
          <ul style={{ margin: 0, paddingLeft: 24 }}>
            {punchoutItemsInCopiedOrder.map((item) => (
              <li key={item.description} style={{ marginBottom: 8 }}>
                {item.description.charAt(0).toUpperCase() + item.description.slice(1).toLowerCase()}
              </li>
            ))}
          </ul>
        </DialogContent>
      </Dialog>
    </div>
  );
}
